import React, { FC, forwardRef } from 'react';
import Link from 'next/link';
import { Body } from '@sumup-oss/circuit-ui';
import { spacing } from '@sumup-oss/circuit-ui/legacy';
import { ChevronRight } from '@sumup-oss/icons';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';

import {
  ReadArticleLink,
  ArticleDescription,
  SectionArticlesItem,
} from '../styled';

import {
  SectionTree,
  SectionArticles as SectionArticleTypes,
} from 'types/common';
import { addSlugToUrl } from 'services/common';
import { ArticleTitle, PageContentTitle } from 'components/styledComponents';
import { SUBPATH_TYPES } from 'constants/common';

type SectionArticlesProps = {
  articles: SectionArticleTypes;
  isPos?: boolean;
  sectionTree: SectionTree;
  currentSection: string;
};

// eslint-disable-next-line prefer-arrow-callback
const ForwardedReadArticleLink = forwardRef(function CustomReadArticleLink(
  {
    href,
    t,
    title,
  }: { href?: string; t: (param: string) => string; title?: string },
  /* eslint-disable @typescript-eslint/no-unused-vars */
  _ref,
) {
  return (
    <ReadArticleLink href={href} aria-label={`${t('read_article')}, ${title}`}>
      <Body as="strong">{t('read_article')}</Body>
      <ChevronRight />
    </ReadArticleLink>
  );
});

const SectionArticles: FC<SectionArticlesProps> = ({
  articles,
  isPos = false,
  sectionTree = {},
  currentSection = '',
}) => {
  const { t } = useTranslation();

  return sectionTree[currentSection] ? (
    <>
      {sectionTree[currentSection]?.sectionImage ? (
        <Image
          src={`https:${sectionTree[currentSection]?.sectionImage?.url}`}
          alt={sectionTree[currentSection].sectionImage.name}
          width={640}
          height={260}
        />
      ) : null}
      <PageContentTitle as="h1" size="m" css={spacing({ top: 'peta' })}>
        {sectionTree[currentSection].sectionTitle}
      </PageContentTitle>
      {Object.keys(articles).map((articleId) => (
        <SectionArticlesItem key={articleId}>
          <ArticleTitle as="h2" size="four">
            {articles[articleId].title}
          </ArticleTitle>
          <ArticleDescription>
            {articles[articleId].articleDescription}
          </ArticleDescription>
          <Link
            passHref
            legacyBehavior
            href={addSlugToUrl({
              subPath: SUBPATH_TYPES.articles,
              contentId: articleId,
              contentSlug: articles[articleId].articleSlug,
              isPos,
            })}
          >
            <ForwardedReadArticleLink t={t} title={articles[articleId].title} />
          </Link>
        </SectionArticlesItem>
      ))}
    </>
  ) : null;
};

export default SectionArticles;
